import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import { Done } from '@mui/icons-material';
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Divider,
  List,
  ListItem,
  useMediaQuery,
  useTheme,
  CircularProgress
} from '@mui/material';

import useSWRMutation from "swr/mutation";

import { useUtils } from '../../../context/UtilsProvider';
import { useClient } from '../../../context/ClientProvider';
import { useAuth } from '../../../context/AuthProvider';
import { useAnalytics } from '../../../context/AnalyticsProvider';

export default function RegOtpLoginCard() {
  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();
  const location = useLocation();
  const {
    formError,
    setFormError,
    createAlert,
    alertSeverityTypes,
    formState,
    onChange,
    resetFormState
  } = useUtils();

  const {
    postSignIn,
    setAuthState,
    authStates,
    registrationReasons,
    startSession,
    otpUserId,
    setOtpUserId,
    otpSession,
    setOtpSession,
    setOtpDestination,
  } = useAuth();

  const navigate = useNavigate();
  const { trigger, isMutating } = useSWRMutation("/auth/signin", postSignIn);
  const { setTag } = useAnalytics();

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const response = await trigger({
        username: formState.email,
        password: formState.password
      });

      console.info("post sign in auth response:", response);

      if (response?.challengeName === "SMS_MFA") {
        setOtpUserId(response.challengeParameters.USER_ID_FOR_SRP);
        setOtpSession(response.session);
        setOtpDestination(response.challengeParameters.CODE_DELIVERY_DESTINATION);
        setAuthState(authStates.confirmOtp);
      } else if (response?.authenticationResult) {
        setFormError(null);
        startSession(response.authenticationResult);
        setTag('login_success');
        navigate("/");
      } else {
        handleLoginFailure();
      }
    } catch (error) {
      console.error("post sign in auth error", error);
      handleLoginFailure();
    }
  };

  const handleLoginFailure = () => {
    createAlert("Hmm... something's not right. Please try again", alertSeverityTypes.error);
    setTag({
      accountReference: otpUserId || "UNKNOWN_REF",
      accountId: 1,
      partyId: 1,
      sessionId: String(otpSession) || "FAILED_SESSION",
      path: location.pathname,
    client: clientConfig?.name || "UNKNOWN_CLIENT",
    tag: "login_failed",
    timestamp: new Date().toISOString(),
  });
  };

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const signUp = () => {
    resetFormState();
    setAuthState(authStates.signUp);
    setFormError(null);
  };

  const forgotPassword = () => {
    setAuthState(authStates.forgotPassword);
    setFormError(null);
  };

  return clientConfig && (
    <Card
      sx={{
        maxWidth: { md: "80%" },
        m: "0px auto",
        borderRadius: "0",
        position: "relative",
        bottom: { xs: "auto", md: "300px" },
        boxShadow: "-9px 10px 4px -1px rgba(0, 0, 0, 0.2)",
      }}
    >
      <Stack
        sx={{ p: 2 }}
        direction={{
          xs: 'column',
          md: 'row',
        }}
        divider={
          <Divider
            orientation={isMdUp ? "vertical" : "horizontal"}
            variant="middle"
            flexItem
          >
            <Typography
              color="text.secondary"
              fontSize={14}
            >
              Or
            </Typography>
          </Divider>
        }
      >
        <CardContent sx={{ textAlign: 'left', width: '100%', display: 'flex', flexDirection: 'column' }}>
          <Typography component="h1" variant="h5" fontWeight={'bold'} marginBottom={1}>
            {clientConfig.layout.auth.login.headerText}
          </Typography>
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
            <Box id="email" sx={{ mb: 2 }}>
              <Typography component="label" htmlFor="email" marginTop={10}>
                Email address
              </Typography>
              <TextField
                margin="dense"
                required
                fullWidth
                id="email"
                label="Enter your Email address"
                name="email"
                autoComplete="email"
                autoFocus
                onChange={onChange}
              />
            </Box>
            <Box id="password" sx={{ mb: 2 }}>
              <Typography component="label" htmlFor="password" marginTop={20}>
                Password
              </Typography>
              <TextField
                margin="dense"
                required
                fullWidth
                name="password"
                label="Enter your password"
                type="password"
                id="password"
                autoComplete="current-password"
                onChange={onChange}
              />
            </Box>

            <Link variant="body2" onClick={forgotPassword}>
              Forgot your password?
            </Link>

            <Box sx={{ mt: 4, mb: { xs: 4, md: 0 }, textAlign: { xs: "center", md: "left" } }}>
              <Button
                type='submit'
                variant='contained'
                disabled={!formState.email || !formState.password}
                sx={{
                  borderRadius: clientConfig.layout.buttonBorderRadius,
                  width: { xs: "60%", md: "50%" },
                  backgroundColor: clientConfig.layout.primaryButtonBackgroundColour,
                  color: clientConfig.layout.primaryButtonColour,
                  "&:hover": {
                    backgroundColor: clientConfig.layout.hoverPrimaryButtonBackgroundColour,
                    color: clientConfig.layout.hoverPrimaryButtonColour,
                  }
                }}
              >
                Log in
              </Button>
            </Box>

            <Box sx={{ position: "relative", mt: 4 }}>
              {isMutating && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
              <Typography
                sx={{ ml: 1, display: "inline-block", mb: { xs: 2 } }}
                color='error'
              >
                {formError && `* ${formError}`}
              </Typography>
            </Box>
          </Box>
        </CardContent>

        <CardContent sx={{ textAlign: 'center', width: '100%', display: 'flex', flexDirection: 'column' }}>
          <Typography component="h1" variant="h5" sx={{ fontWeight: 'bold', mt: 4 }}>
            Create an account
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Box
              id="registrationReasons"
              sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
              <List sx={{ textAlign: "center", mt: 3, ml: { xs: 3, md: 2, xl: 3 } }}>
                {registrationReasons.map((item) => (
                  <ListItem key={item}>
                    <Done sx={{ color: 'green', marginRight: 1 }} />
                    <Typography component='p' sx={{ fontSize: { xs: 16, sm: 17, xl: 18 } }}>
                      {item}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
            <Box sx={{ mt: 10, textAlign: { xs: "center", md: "left" } }}>
              <Button
                variant='contained'
                sx={{
                  borderRadius: clientConfig.layout.buttonBorderRadius,
                  width: { xs: "60%", md: "50%" },
                  backgroundColor: clientConfig.layout.primaryButtonBackgroundColour,
                  color: clientConfig.layout.primaryButtonColour,
                  "&:hover": {
                    backgroundColor: clientConfig.layout.hoverPrimaryButtonBackgroundColour,
                    color: clientConfig.layout.hoverPrimaryButtonColour,
                  }
                }}
                onClick={signUp}
              >
                Register
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Stack>
    </Card>
  );
}
